import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Alert from './Alert';
import './QuotationPreviewModal.css';

const QuotationPreviewModal = ({ quotationId, onClose }) => {
    const [quotation, setQuotation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotationId]);

    const fetchQuotation = async () => {
        try {
            const response = await api.get(`/professional-quotations/${quotationId}`);
            setQuotation(response.data.quotation);
        } catch (err) {
            setError('Failed to load quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (section, field, value) => {
        setQuotation(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleWorkStageChange = (index, field, value) => {
        const newWorkStages = [...quotation.workStages];
        newWorkStages[index][field] = value;
        setQuotation(prev => ({ ...prev, workStages: newWorkStages }));
    };

    const handleChargeStageChange = (index, field, value) => {
        const newChargeStages = [...quotation.charges.stages];
        newChargeStages[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
        setQuotation(prev => ({
            ...prev,
            charges: { ...prev.charges, stages: newChargeStages }
        }));
    };

    const handleArrayChange = (field, index, value) => {
        const newArray = [...quotation[field]];
        newArray[index] = value;
        setQuotation(prev => ({ ...prev, [field]: newArray }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            const cleanedData = {
                ...quotation,
                generalAccord: quotation.generalAccord?.filter(item => item.trim() !== '') || [],
                designServices: quotation.designServices?.filter(item => item.trim() !== '') || []
            };

            const response = await api.put(`/professional-quotations/${quotationId}`, cleanedData);

            if (response.data.success) {
                setSuccess('Changes saved successfully!');
                setTimeout(() => {
                    onClose(true);
                }, 1000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="quotation-preview-modal-overlay">
                <div className="quotation-preview-modal">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <div className="spinner"></div>
                        <p>Loading preview...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!quotation) return null;

    return (
        <div className="quotation-preview-modal-overlay">
            <div className="quotation-preview-modal">
                {/* Header */}
                <div className="modal-header">
                    <div>
                        <h2>📋 Project Assurance Preview</h2>
                        <p>Review and edit your quotation details below</p>
                    </div>
                    <button className="close-btn" onClick={() => onClose(false)}>✕</button>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                {/* Scrollable Content */}
                <div className="modal-content">
                    {/* Quotation Info */}
                    <div className="preview-section">
                        <h3>📄 Quotation Information</h3>
                        <div className="info-grid">
                            <div>
                                <strong>Quotation Number:</strong> {quotation.quotationNumber}
                            </div>
                            <div>
                                <strong>Date:</strong> {new Date(quotation.quotationDate).toLocaleDateString()}
                            </div>
                        </div>
                    </div>

                    {/* Project Details */}
                    <div className="preview-section">
                        <h3>🏗️ Project Information</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <label>Project Type *</label>
                                <select
                                    value={quotation.projectDetails.projectType}
                                    onChange={(e) => handleChange('projectDetails', 'projectType', e.target.value)}
                                >
                                    <option value="Residential">Residential</option>
                                    <option value="Hospital">Hospital</option>
                                    <option value="Commercial">Commercial</option>
                                    <option value="Office">Office</option>
                                </select>
                            </div>
                            <div className="form-field">
                                <label>Project Name *</label>
                                <input
                                    type="text"
                                    value={quotation.projectDetails.projectName}
                                    onChange={(e) => handleChange('projectDetails', 'projectName', e.target.value)}
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Project Location *</label>
                                <input
                                    type="text"
                                    value={quotation.projectDetails.projectLocation}
                                    onChange={(e) => handleChange('projectDetails', 'projectLocation', e.target.value)}
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Physical Address *</label>
                                <textarea
                                    value={quotation.projectDetails.projectAddress}
                                    onChange={(e) => handleChange('projectDetails', 'projectAddress', e.target.value)}
                                    rows="2"
                                />
                            </div>
                            <div className="form-field">
                                <label>Project Area (Sqft) *</label>
                                <input
                                    type="number"
                                    value={quotation.projectDetails.projectArea}
                                    onChange={(e) => handleChange('projectDetails', 'projectArea', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Client Details */}
                    <div className="preview-section">
                        <h3>👤 Client Information</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <label>Client Name *</label>
                                <input
                                    type="text"
                                    value={quotation.clientDetails.name}
                                    onChange={(e) => handleChange('clientDetails', 'name', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Cell Phone *</label>
                                <input
                                    type="text"
                                    value={quotation.clientDetails.cellPhone}
                                    onChange={(e) => handleChange('clientDetails', 'cellPhone', e.target.value)}
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Physical Address *</label>
                                <textarea
                                    value={quotation.clientDetails.physicalAddress}
                                    onChange={(e) => handleChange('clientDetails', 'physicalAddress', e.target.value)}
                                    rows="2"
                                />
                            </div>
                            <div className="form-field">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={quotation.clientDetails.email}
                                    onChange={(e) => handleChange('clientDetails', 'email', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Designer Details */}
                    <div className="preview-section">
                        <h3>🎨 Designer/Architect Information</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <label>Firm Name *</label>
                                <input
                                    type="text"
                                    value={quotation.designerDetails.firmName}
                                    onChange={(e) => handleChange('designerDetails', 'firmName', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Head Designer/Architect *</label>
                                <input
                                    type="text"
                                    value={quotation.designerDetails.headDesignerName}
                                    onChange={(e) => handleChange('designerDetails', 'headDesignerName', e.target.value)}
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Physical Address *</label>
                                <textarea
                                    value={quotation.designerDetails.physicalAddress}
                                    onChange={(e) => handleChange('designerDetails', 'physicalAddress', e.target.value)}
                                    rows="2"
                                />
                            </div>
                            <div className="form-field">
                                <label>Cell Phone *</label>
                                <input
                                    type="text"
                                    value={quotation.designerDetails.cellPhone}
                                    onChange={(e) => handleChange('designerDetails', 'cellPhone', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    value={quotation.designerDetails.email}
                                    onChange={(e) => handleChange('designerDetails', 'email', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>License Number</label>
                                <input
                                    type="text"
                                    value={quotation.designerDetails.licenseNumber || ''}
                                    onChange={(e) => handleChange('designerDetails', 'licenseNumber', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Work Stages */}
                    <div className="preview-section">
                        <h3>📋 Work Stages</h3>
                        {quotation.workStages.map((stage, index) => (
                            <div key={index} className="work-stage-item">
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Stage {stage.stageNumber} Name *</label>
                                        <input
                                            type="text"
                                            value={stage.stageName}
                                            onChange={(e) => handleWorkStageChange(index, 'stageName', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field full-width">
                                        <label>Description</label>
                                        <textarea
                                            value={stage.description || ''}
                                            onChange={(e) => handleWorkStageChange(index, 'description', e.target.value)}
                                            rows="2"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Inception of Work */}
                    <div className="preview-section">
                        <h3>💼 2.3 Inception of Work</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <label>Total Fees (₹) *</label>
                                <input
                                    type="number"
                                    value={quotation.inceptionOfWork.totalFees}
                                    onChange={(e) => handleChange('inceptionOfWork', 'totalFees', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Per Sqft Rate (₹) *</label>
                                <input
                                    type="number"
                                    value={quotation.inceptionOfWork.perSqftRate}
                                    onChange={(e) => handleChange('inceptionOfWork', 'perSqftRate', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Notes</label>
                                <textarea
                                    value={quotation.inceptionOfWork.notes || ''}
                                    onChange={(e) => handleChange('inceptionOfWork', 'notes', e.target.value)}
                                    rows="4"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Charges */}
                    <div className="preview-section">
                        <h3>💰 Charges</h3>
                        {quotation.charges.stages.map((charge, index) => (
                            <div key={index} className="charge-item">
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>{charge.stageName} *</label>
                                        <input
                                            type="number"
                                            value={charge.amount}
                                            onChange={(e) => handleChargeStageChange(index, 'amount', e.target.value)}
                                            placeholder="Amount in ₹"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* General Accord */}
                    <div className="preview-section">
                        <h3>📜 General Accord</h3>
                        {quotation.generalAccord?.map((point, index) => (
                            <div key={index} className="form-field full-width">
                                <label>Point {index + 1}</label>
                                <textarea
                                    value={point}
                                    onChange={(e) => handleArrayChange('generalAccord', index, e.target.value)}
                                    rows="2"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Design Services */}
                    <div className="preview-section">
                        <h3>🎨 Design Services</h3>
                        {quotation.designServices?.map((service, index) => (
                            <div key={index} className="form-field full-width">
                                <label>Service {index + 1}</label>
                                <textarea
                                    value={service}
                                    onChange={(e) => handleArrayChange('designServices', index, e.target.value)}
                                    rows="2"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Close Out Period */}
                    <div className="preview-section">
                        <h3>⏱️ Close Out Period</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <label>Duration (Months) *</label>
                                <input
                                    type="number"
                                    value={quotation.closeOutMonths}
                                    onChange={(e) => setQuotation(prev => ({ ...prev, closeOutMonths: parseFloat(e.target.value) || 0 }))}
                                    step="0.1"
                                />
                            </div>
                            <div className="form-field full-width">
                                <label>Description</label>
                                <textarea
                                    value={quotation.closeOutPeriod || ''}
                                    onChange={(e) => setQuotation(prev => ({ ...prev, closeOutPeriod: e.target.value }))}
                                    rows="3"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={() => onClose(false)}
                        disabled={saving}
                    >
                        Close Without Saving
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? '⏳ Saving Changes...' : '✅ Save & Continue'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuotationPreviewModal;
